import { useEffect, useState } from 'react';
import { useDrag } from 'react-use-gesture';
import useResizeObserver from 'use-resize-observer';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import useVelocitySpring from './useVelocitySpring';
import {
  findNearestNumberInArray,
  projection,
  rubberBandIfOutOfBounds,
} from '../Utils/spring';

const gesturesThreshold = 10;

const springs = {
  normal: {
    tension: 247,
    friction: 23,
  },
  damped: {
    tension: 247,
    friction: 33,
  },
};

const useDragable = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [{ y: position }, setPosition] = useVelocitySpring(() => ({
    y: 0,
    config: springs.normal,
  }));
  const { ref, height: refHeight } = useResizeObserver<HTMLDivElement>();
  const openPosition = refHeight ? -refHeight : 0;
  const positions = [0, openPosition];

  const setOpen = () => {
    setPosition({
      y: positions[1],
      config: springs.damped,
      immediate: false,
      onRest: false,
    });

    setIsOpen(true);
  };

  const setClosed = ({ immediate = false } = {}) => {
    setPosition({
      y: positions[0],
      config: springs.damped,
      immediate: immediate,
      onRest: false,
    });

    setIsOpen(false);
  };

  const getIsOpen = () => {
    const nearestPoint = findNearestNumberInArray(position.value, positions);
    const isOpen = nearestPoint === positions[1];

    return isOpen;
  };

  // Drag gesture config
  const bind = useDrag(
    ({ event, last, memo, movement: [mx, my], vxvy: [, vy] }) => {
      // Can't figure our way ts thinks there is no event.target.id
      // @ts-expect-error
      if (event && event.target.id === 'grabber' && ref.current) {
        event.preventDefault();

        const isClick = last && Math.abs(mx) + Math.abs(my) <= 3;

        if (isClick) {
          enableBodyScroll(ref.current);
          return getIsOpen() ? setClosed() : setOpen();
        }

        if (!memo) {
          const isIntentionalGesture =
            Math.abs(my) > gesturesThreshold && Math.abs(my) > Math.abs(mx);

          if (!isIntentionalGesture) return disableBodyScroll(ref.current);

          memo = position.value - my;
        }

        if (last) {
          enableBodyScroll(ref.current);

          const projectedEndpoint = position.value + projection(vy);
          const nearestPoint = findNearestNumberInArray(projectedEndpoint, positions);

          const handleOnRest = () => {
            // Recalculate nearestPoint with updated y.value to determine if open or not
            if (getIsOpen() !== isOpen) setIsOpen(getIsOpen());
          };

          return setPosition({
            y: nearestPoint,
            immediate: false,
            config: springs.normal,
            onRest: handleOnRest,
          });
        }

        const newY = rubberBandIfOutOfBounds(
          positions[positions.length - 1],
          positions[0],
          my + memo,
          0.08
        );

        setPosition({
          y: newY,
          immediate: true,
          onRest: false,
        });

        return memo;
      }
    },
    {
      domTarget: ref,
      event: { passive: false },
    }
  );

  // Reset open state when refHeight changes
  useEffect(() => {
    if (isOpen) {
      setPosition({
        y: positions[1],
        config: springs.damped,
        immediate: false,
        onRest: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refHeight]);

  return { bind, ref, position, isOpen, setOpen, setClosed };
};

export default useDragable;
